/**
 * buildTools.ts — Task D6
 *
 * Composes all make*Tool factories into the full advisor tool surface.
 * Called by the useAdvisor hook (E1) once per panel mount.
 *
 * Tool order (test-asserted verbatim):
 *   search_knowledge, describe_page, query_kpi,
 *   set_kpi, set_timeframe, set_filters, set_group_by, set_value_types, set_columns, set_campaign,
 *   propose_navigation
 *
 * makeSteerTools reads the model floor via its own default (getModelFloorIso, the live store
 * accessor) so no extra ctx plumbing is needed here for the set_timeframe floor clamp.
 */

import type { ApiClient } from '../../api/apiClient'
import type { AdvisorTool } from '../runtime/types'
import type { KnowledgeProvider } from '../grounding/types'
import type { ViewSnapshot } from '../grounding/currentView'
import type { DatasetOverview } from '../grounding/datasetOverview'
import type { RouteFamily } from '../../state/routeStateContract'

import { makeSearchKnowledgeTool } from './searchKnowledge'
import { makeDescribePageTool } from './describePage'
import { makeQueryKpiTool } from './queryKpi'
import { makeSteerTools } from './steer'
import { makeProposeNavigationTool } from './proposeNavigation'

// ---------------------------------------------------------------------------
// AdvisorContext — injected by the panel / useAdvisor hook
// ---------------------------------------------------------------------------

export interface AdvisorContext {
  apiClient: ApiClient
  setParams: (next: Record<string, string | null>) => void
  getParams: () => URLSearchParams
  getView: () => ViewSnapshot
  kb: KnowledgeProvider
  /** Latest built Dataset Overview, or null while loading / after a failed build. */
  getOverview: () => DatasetOverview | null
  /** FU-49: live route family, read at tool-run time by the steer route gate. */
  getRouteFamily: () => RouteFamily | null
}

// ---------------------------------------------------------------------------
// buildTools — compose the full tool surface
// ---------------------------------------------------------------------------

export function buildTools(ctx: AdvisorContext): AdvisorTool[] {
  return [
    makeSearchKnowledgeTool(ctx.kb),
    makeDescribePageTool({ apiClient: ctx.apiClient }),
    makeQueryKpiTool({
      apiClient: ctx.apiClient,
      getView: ctx.getView,
      getOverview: ctx.getOverview,
    }),
    ...makeSteerTools({
      setParams: ctx.setParams,
      getParams: ctx.getParams,
      getOverview: ctx.getOverview,
      getRouteFamily: ctx.getRouteFamily,
    }),
    makeProposeNavigationTool(),
  ]
}
